import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useCrud from "../../../../hooks/useCrud";

const sourceLabels = {
  CARD: "Karta",
  PIN: "PIN",
  MANUAL: "Ręcznie",
  IMPORT: "Import",
  MOCK: "Dane testowe",
  SIMULATOR: "Symulator terminala",
  TERMINAL: "Terminal fizyczny",
  LEGACY: "Historia wcześniejsza"
};

const pad2 = value => String(value).padStart(2, "0");

const toDateValue = date => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const toMonthValue = date => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;

const getIsoWeekValue = date => {
  const current = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = current.getDay() || 7;
  current.setDate(current.getDate() + 4 - day);
  const yearStart = new Date(current.getFullYear(), 0, 1);
  const week = Math.ceil((((current - yearStart) / 86400000) + 1) / 7);
  return `${current.getFullYear()}-W${pad2(week)}`;
};

const isoWeekToMonday = value => {
  const match = /^(\d{4})-W(\d{2})$/.exec(value || "");
  if (!match) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  const januaryFourth = new Date(year, 0, 4);
  const day = januaryFourth.getDay() || 7;
  const monday = new Date(year, 0, 4 - day + 1);
  monday.setDate(monday.getDate() + (week - 1) * 7);
  return monday;
};

const addDays = (date, days) => {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setDate(result.getDate() + days);
  return result;
};

const resolveRange = (periodType, dayValue, weekValue, monthValue, yearValue, customFrom, customTo) => {
  if (periodType === "DAY") {
    return dayValue ? {dateFrom: dayValue, dateTo: dayValue} : null;
  }

  if (periodType === "WEEK") {
    const monday = isoWeekToMonday(weekValue);
    return monday ? {dateFrom: toDateValue(monday), dateTo: toDateValue(addDays(monday, 6))} : null;
  }

  if (periodType === "MONTH") {
    const match = /^(\d{4})-(\d{2})$/.exec(monthValue || "");
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const lastDay = new Date(year, month, 0).getDate();
    return {dateFrom: `${year}-${pad2(month)}-01`, dateTo: `${year}-${pad2(month)}-${pad2(lastDay)}`};
  }

  if (periodType === "YEAR") {
    const year = Number(yearValue);
    if (!Number.isInteger(year) || year < 1900 || year > 2200) return null;
    return {dateFrom: `${year}-01-01`, dateTo: `${year}-12-31`};
  }

  if (periodType === "CUSTOM") {
    if (!customFrom || !customTo || customFrom > customTo) return null;
    return {dateFrom: customFrom, dateTo: customTo};
  }

  return null;
};

const TabEmployeeWorkLog = ({employeeId, locationsData}) => {
  const dataUrl = "/rcp-events/sessions/employee/" + employeeId;
  const allPositionsUrl = "/positions";
  const timeCardsUrl = "/time-cards";
  const navigate = useNavigate();
  const location = useLocation();
  const [employeeWorkLog, setEmployeeWorkLog] = useState([]);
  const [allPositionsData, setAllPositionsData] = useState([]);
  const [timeCardsData, setTimeCardsData] = useState([]);
  const [rcpStatistics, setRcpStatistics] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodType, setPeriodType] = useState("MONTH");
  const [dayValue, setDayValue] = useState(() => toDateValue(new Date()));
  const [weekValue, setWeekValue] = useState(() => getIsoWeekValue(new Date()));
  const [monthValue, setMonthValue] = useState(() => toMonthValue(new Date()));
  const [yearValue, setYearValue] = useState(() => String(new Date().getFullYear()));
  const [customFrom, setCustomFrom] = useState(() => {
    const today = new Date();
    return toDateValue(new Date(today.getFullYear(), today.getMonth(), 1));
  });
  const [customTo, setCustomTo] = useState(() => toDateValue(new Date()));

  const {getItems} = useCrud(dataUrl);
  const headersMap = {
    positionId: "Stanowisko",
    locationId: "Lokalizacja",
    date: "Data",
    workStart: "Początek pracy",
    workEnd: "Koniec pracy",
    minutesWorked: "Czas pracy",
    breakMinutes: "Przerwy",
    source: "Źródło",
    timeCardId: "Numer karty RFID",
    complete: "Stan",
    cost: "Koszt"
  };

  const columns = Object.keys(headersMap);
  const range = useMemo(
    () => resolveRange(periodType, dayValue, weekValue, monthValue, yearValue, customFrom, customTo),
    [periodType, dayValue, weekValue, monthValue, yearValue, customFrom, customTo]
  );
  const invalidCustomRange = periodType === "CUSTOM" && customFrom && customTo && customFrom > customTo;

  const formatDate = dateStr => dateStr ? new Date(`${dateStr}T12:00:00`).toLocaleDateString("pl-PL") : "";
  const formatTime = dateStr => dateStr ? new Date(dateStr).toLocaleTimeString("pl-PL", {hour: "2-digit", minute: "2-digit"}) : "";
  const formatMinutes = value => value == null ? "" : `${Math.floor(Number(value) / 60)} h ${Number(value) % 60} min`;

  const getPositionName = id => {
    const pos = allPositionsData.find(item => Number(item.id) === Number(id));
    return pos ? pos.name : id ?? "";
  };

  const getTimeCardNumber = (id, item) => {
    if (item.cardId) return item.cardId;
    const card = timeCardsData.find(entry => Number(entry.id) === Number(id));
    return card ? card.cardId : id ?? "";
  };

  const getLocationName = id => {
    if (!locationsData) return id ?? "";
    const loc = locationsData.find(item => Number(item.id) === Number(id));
    return loc ? loc.name : id ?? "";
  };

  const renderCell = (col, item) => {
    switch (col) {
      case "date": return formatDate(item[col]);
      case "workStart":
      case "workEnd": return formatTime(item[col]);
      case "positionId": return getPositionName(item[col]);
      case "timeCardId": return getTimeCardNumber(item[col], item);
      case "locationId": return getLocationName(item[col]);
      case "minutesWorked":
      case "breakMinutes": return formatMinutes(item[col]);
      case "source": return sourceLabels[item[col]] ?? item[col] ?? "";
      case "complete": return item.complete === false ? "Niepełny zapis" : "Zakończony";
      case "cost": return item.cost == null ? "" : `${Number(item.cost).toLocaleString("pl-PL", {minimumFractionDigits: 2, maximumFractionDigits: 2})} zł`;
      default: return item[col] ?? "";
    }
  };

  useEffect(() => {
    let active = true;

    const getReferenceData = async () => {
      const [positionsData, cardsData] = await Promise.all([
        getItems(allPositionsUrl),
        getItems(timeCardsUrl)
      ]);

      if (!active) return;
      if (positionsData.status === 200 && cardsData.status === 200) {
        setAllPositionsData(positionsData.data);
        setTimeCardsData(cardsData.data);
      } else {
        navigate("/login", {state: {from: location}, replace: true});
      }
    };

    getReferenceData();
    return () => {
      active = false;
    };
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId || !range) {
      setEmployeeWorkLog([]);
      setRcpStatistics(null);
      setPeriodLoading(false);
      return;
    }
    let active = true;

    const getPeriodData = async () => {
      setPeriodLoading(true);
      const query = `?dateFrom=${encodeURIComponent(range.dateFrom)}&dateTo=${encodeURIComponent(range.dateTo)}`;
      const [itemsData, statisticsData] = await Promise.all([
        getItems(`${dataUrl}${query}`),
        getItems(`/rcp-events/statistics/employee/${employeeId}${query}`)
      ]);

      if (!active) return;
      setPeriodLoading(false);

      if (itemsData.status === 200 && statisticsData.status === 200) {
        setEmployeeWorkLog(itemsData.data || []);
        setRcpStatistics(statisticsData.data || null);
      } else {
        navigate("/login", {state: {from: location}, replace: true});
      }
    };

    getPeriodData();
    return () => {
      active = false;
    };
  }, [employeeId, range?.dateFrom, range?.dateTo]);

  const summary = useMemo(() => employeeWorkLog.reduce((result, item) => {
    result.minutes += Number(item.minutesWorked || 0);
    result.breakMinutes += Number(item.breakMinutes || 0);
    result.cost += Number(item.cost || 0);
    result.incomplete += item.complete === false ? 1 : 0;
    return result;
  }, {minutes: 0, breakMinutes: 0, cost: 0, incomplete: 0}), [employeeWorkLog]);

  return (
    <div className="d-flex flex-column" style={{height: "100vh", overflow: "hidden"}}>
      <div className="card shadow-sm p-2 mb-2">
        <div className="d-flex flex-wrap align-items-end gap-2">
          <div style={{minWidth: "170px"}}>
            <label className="form-label mb-1">Okres analizy</label>
            <select className="form-select" value={periodType} onChange={event => setPeriodType(event.target.value)}>
              <option value="DAY">Dzień</option>
              <option value="WEEK">Tydzień</option>
              <option value="MONTH">Miesiąc</option>
              <option value="YEAR">Rok</option>
              <option value="CUSTOM">Dowolny okres</option>
            </select>
          </div>

          {periodType === "DAY" && <div>
            <label className="form-label mb-1">Dzień</label>
            <input className="form-control" type="date" value={dayValue} onChange={event => setDayValue(event.target.value)}/>
          </div>}

          {periodType === "WEEK" && <div>
            <label className="form-label mb-1">Tydzień</label>
            <input className="form-control" type="week" value={weekValue} onChange={event => setWeekValue(event.target.value)}/>
          </div>}

          {periodType === "MONTH" && <div>
            <label className="form-label mb-1">Miesiąc</label>
            <input className="form-control" type="month" value={monthValue} onChange={event => setMonthValue(event.target.value)}/>
          </div>}

          {periodType === "YEAR" && <div style={{maxWidth: "130px"}}>
            <label className="form-label mb-1">Rok</label>
            <input className="form-control" type="number" min="1900" max="2200" value={yearValue} onChange={event => setYearValue(event.target.value)}/>
          </div>}

          {periodType === "CUSTOM" && <>
            <div>
              <label className="form-label mb-1">Od</label>
              <input className="form-control" type="date" value={customFrom} onChange={event => setCustomFrom(event.target.value)}/>
            </div>
            <div>
              <label className="form-label mb-1">Do</label>
              <input className="form-control" type="date" value={customTo} onChange={event => setCustomTo(event.target.value)}/>
            </div>
          </>}

          {range && <div className="ms-auto small text-muted pb-2">
            Analizowany okres: <strong>{formatDate(range.dateFrom)} – {formatDate(range.dateTo)}</strong>
          </div>}
        </div>
        {invalidCustomRange && <div className="err_info mt-2">Data końcowa nie może być wcześniejsza niż data początkowa.</div>}
      </div>

      <div className="row flex-grow-1" style={{minHeight: 0, overflow: "hidden"}}>
        <div className="col-md-3 d-flex flex-column" style={{height: "100%"}}>
          <div style={{background: "#f5f5f5", borderRadius: "5px", padding: "10px", marginBottom: "10px"}}>
            <strong>Podsumowanie RCP</strong>
            {periodLoading && <p className="text-muted mt-2 mb-2">Ładowanie danych...</p>}
            {!periodLoading && rcpStatistics && <div className="border-bottom pb-2 mb-2">
              <p className="mb-1"><strong>Spóźnienia w okresie: {rcpStatistics.lateCount || 0}</strong></p>
              <p className="mb-1">Łącznie minut spóźnień: {rcpStatistics.totalLateMinutes || 0}</p>
              <p className="mb-1">Średnie / największe: {rcpStatistics.averageLateMinutes || 0} / {rcpStatistics.maxLateMinutes || 0} min</p>
              <p className="mb-1">Punktualność: {rcpStatistics.punctualityPercent == null ? "—" : `${rcpStatistics.punctualityPercent}%`}</p>
              <small className="text-muted">Zakres {formatDate(rcpStatistics.dateFrom)} – {formatDate(rcpStatistics.dateTo)}; statystyka porównuje opublikowany grafik z RCP.</small>
            </div>}
            <p>Łączny czas: {formatMinutes(summary.minutes)}</p>
            <p>Łączne przerwy: {formatMinutes(summary.breakMinutes)}</p>
            <p>Niepełne sesje: {summary.incomplete}</p>
            <p>Łączny koszt: {summary.cost.toLocaleString("pl-PL", {minimumFractionDigits: 2, maximumFractionDigits: 2})} zł</p>
            <small className="text-muted">Widok łączy nowe zdarzenia RCP z wcześniejszą historią zapisaną w employee_work_log.</small>
          </div>
        </div>

        <div className="col-md-9 d-flex flex-column" style={{height: "100%"}}>
          <div style={{flex: 1, overflowY: "auto"}}>
            <table className="table table-striped">
              <thead style={{position: "sticky", top: 0, background: "#fff", zIndex: 1}}>
                <tr>{columns.map(col => <th key={col}>{headersMap[col]}</th>)}</tr>
              </thead>
              <tbody>
                {!periodLoading && employeeWorkLog.length === 0 && <tr>
                  <td colSpan={columns.length} className="text-center text-muted py-4">Brak danych w wybranym okresie.</td>
                </tr>}
                {employeeWorkLog.map((item, index) => (
                  <tr key={item.key || `${item.employeeId}-${item.workStart}-${index}`}>{columns.map(col => <td key={col}>{renderCell(col, item)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabEmployeeWorkLog;
